import {
  Box,
  Collapse,
  Divider,
  HStack,
  Icon,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FiInfo, FiMapPin, FiTrendingDown, FiTrendingUp } from "react-icons/fi";
import { FaUserFriends } from "react-icons/fa";
import type {
  CampaignIntent,
  DisplayMarketingAdSet,
  DisplayMarketingCampaign,
} from "common/interfaces/KYDMarketing";
import { CampaignDetailLink } from "./CampaignDetailLink";
import { PerformanceTierBadge } from "./PerformanceTierBadge";
import {
  formatAudience,
  formatLocation,
  getMetric,
  getInsightItems,
  getInsightVariantColors,
  getInsightActionLabel,
  hasCustomAudiences,
} from "../adSetCardUtils";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import { Button } from "./Button";
import { LabelValueRow } from "./LabelValueRow";
import MetricsCard from "./MetricsCard";
import { AdDetailBlock } from "./AdDetailBlock";
import { KYDAdminCampaign } from "@common/interfaces/KYDEvent";
import { StatusBadge } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/StatusBadge";

function TargetingRow({ adset }: { adset: DisplayMarketingAdSet }) {
  if (!hasCustomAudiences(adset)) return null;
  return (
    <LabelValueRow
      icon={FaUserFriends}
      label="Audience"
      value={formatAudience(adset)}
    />
  );
}

function intentsForAd(
  intents: CampaignIntent[],
  ad: { id?: string; external_id?: string; intents?: CampaignIntent[] }
): CampaignIntent[] {
  if (ad.intents?.length != null && ad.intents.length > 0) {
    return ad.intents;
  }
  const adId = ad.id ?? ad.external_id;
  if (!adId) return [];
  return intents.filter((i) =>
    i.actions?.some(
      (a) =>
        a.entity_type === "ad" &&
        (a.entity_id === ad.id || a.entity_id === ad.external_id)
    )
  );
}

export default function AdSetCard({
  adset,
  campaign,
  pages,
  instagramAccounts,
  intentsForAdset,
  intents = [],
  onEditAdset,
  onEditAd,
  onPublishAdset,
  onPublishAd,
  onUnpublishAdset,
  onUnpublishAd,
  activatingAdKey,
  isPublishingCampaign,
  pausingAdsetId,
  publishingAdsetId,
  pausingAdKey,
  onRefetch,
  onApplyIntent,
  onIgnoreIntent,
  applyingIntentId,
  ignoringIntentId,
  onOpenWizardReview,
  refCallback,
}: {
  adset: DisplayMarketingAdSet;
  campaign: DisplayMarketingCampaign;
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  intentsForAdset: CampaignIntent[];
  intents?: CampaignIntent[];
  onEditAdset: (adsetId: string) => void;
  /** Open the wizard on the ad creative form for the given ad (advanced flow). */
  onEditAd?: (
    ad: import("@common/interfaces/KYDMarketing").DisplayMarketingAd
  ) => void;
  onPublishAdset: (p: { campaign_id: string; adset_id: string }) => void;
  onPublishAd?: (p: {
    campaign_id: string;
    adset_id: string;
    ad_id: string;
  }) => void | Promise<void>;
  onUnpublishAdset: (p: { campaign_id: string; adset_id: string }) => void;
  onUnpublishAd: (p: {
    campaign_id: string;
    adset_id: string;
    ad_id: string;
  }) => void | Promise<void>;
  /** Composite key "adsetId:adId" for the ad currently being activated. */
  activatingAdKey?: string | null;
  /** Campaign-level activate action loading state. */
  isPublishingCampaign?: boolean;
  /** Ad set id currently being paused (show loading on this card's Pause button). */
  pausingAdsetId?: string | null;
  /** Ad set id currently being activated (show loading on this card's Activate button). */
  publishingAdsetId?: string | null;
  /** Composite key "adsetId:adId" for the ad currently being paused. */
  pausingAdKey?: string | null;
  onRefetch: () => void;
  onApplyIntent?: (intent: CampaignIntent) => void | Promise<void>;
  onIgnoreIntent?: (intent: CampaignIntent) => void | Promise<void>;
  /** Intent id currently being applied (show loading on that link). */
  applyingIntentId?: string | null;
  /** Intent id currently being ignored (show "Ignoring…" on that link). */
  ignoringIntentId?: string | null;
  /** Open the ads manager wizard on the review step (e.g. for "View" on frequency). */
  onOpenWizardReview?: (adsetId: string) => void;
  /** Callback ref to register this card's root element (e.g. for scroll-into-view). */
  refCallback?: (el: HTMLElement | null) => void;
}) {
  const hasAnyAdWithIntent = (adset.ads ?? []).some(
    (ad) => intentsForAd(intents, ad).length > 0
  );
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: hasAnyAdWithIntent,
  });
  const metrics = adset.metrics as KYDAdminCampaign;
  const isFrozen = adset.status === "FROZEN";
  const insightItems = getInsightItems({
    entityType: "adset",
    entity: adset,
    intents: intentsForAdset,
  });

  const metricsCardItems = [
    { id: "spend", label: "Spend", value: getMetric(metrics, "display_spend") },
    {
      id: "roas",
      label: "ROAS",
      value: getMetric(metrics, "display_kyd_roas"),
    },
    { id: "ctr", label: "Avg. CTR", value: getMetric(metrics, "display_ctr") },
    {
      id: "cpm",
      label: "Avg. CPM",
      value: getMetric(metrics, "display_cpm"),
    },
  ];

  const handlePause = () => {
    if (adset.status === "ACTIVE") {
      onUnpublishAdset({ campaign_id: campaign.id, adset_id: adset.id });
    }
  };

  const handleActivate = () => {
    if (adset.status === "PAUSED") {
      onPublishAdset({ campaign_id: campaign.id, adset_id: adset.id });
    }
  };

  const isPausingAdset = pausingAdsetId === adset.id;
  const isPublishingAdset = publishingAdsetId === adset.id;

  return (
    <VStack
      ref={refCallback}
      w="full"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      p={6}
      pb={2}
      align="stretch"
      boxShadow="0 2px 12px rgba(0,0,0,0.06)"
      bg="#F5F5F5"
      fontFamily="sans-serif"
      flexGrow={1}
      gap={2}
      scrollMarginTop={6}
    >
      <HStack justify="space-between" align="center" flexWrap="wrap">
        <HStack gap={3} flexWrap="wrap" align="center" justify={"flex-start"}>
          <Text
            fontFamily="Work Sans"
            fontWeight="600"
            fontSize="18px"
            lineHeight="130%"
            letterSpacing="0"
            verticalAlign="middle"
            color="gray.800"
          >
            {adset.name}
          </Text>
          <HStack>
            <StatusBadge status={adset.status} />
            <PerformanceTierBadge performance={adset.performance} />
          </HStack>
        </HStack>
        <HStack gap={2} flexShrink={0}>
          <Button onClick={() => onEditAdset(adset.id)} isDisabled={isFrozen}>
            Edit
          </Button>
          {adset.status === "ACTIVE" && (
            <Button
              onClick={handlePause}
              isLoading={isPausingAdset}
              isDisabled={isPausingAdset}
            >
              Pause
            </Button>
          )}
          {adset.status === "PAUSED" && (
            <Button
              onClick={handleActivate}
              isLoading={isPublishingAdset}
              isDisabled={isPublishingAdset || isPublishingCampaign}
            >
              Activate
            </Button>
          )}
        </HStack>
      </HStack>

      <VStack align="stretch" fontSize="sm" gap={0}>
        <TargetingRow adset={adset} />
        <LabelValueRow
          icon={FiMapPin}
          label="Location"
          value={formatLocation(adset)}
        />
      </VStack>

      <MetricsCard items={metricsCardItems}>
        {insightItems.length > 0 && (
          <VStack align="stretch" spacing={2} w="100%">
            {insightItems.map((item, index) => {
              const colors = getInsightVariantColors(item.variant);
              return (
                <Box key={index}>
                  {index > 0 && (
                    <Divider
                      borderColor="gray.200"
                      mb={2}
                      borderBottomWidth="1px"
                    />
                  )}
                  <HStack spacing={3} align="center" w="100%">
                    <Box
                      borderRadius="8px"
                      bg={colors.bg}
                      p={1.5}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon
                        as={
                          item.variant === "positive"
                            ? FiTrendingUp
                            : item.variant === "negative"
                              ? FiTrendingDown
                              : FiInfo
                        }
                        color={colors.color}
                        boxSize={5}
                      />
                    </Box>
                    <Tooltip
                      label={item.fullExplanation}
                      hasArrow
                      placement="top"
                      maxW="360px"
                      whiteSpace="pre-wrap"
                      isDisabled={!item.fullExplanation}
                    >
                      <Text
                        fontSize="sm"
                        fontWeight={600}
                        color="gray.600"
                        flex={1}
                        lineHeight="130%"
                        noOfLines={1}
                        cursor={item.fullExplanation ? "help" : undefined}
                      >
                        {item.line}
                      </Text>
                    </Tooltip>
                    <HStack gap={1} align="center" flexShrink={0}>
                      {item.intent.intent_type !== "TOO_EARLY_TO_TELL" && (
                        <>
                          <CampaignDetailLink
                            onClick={(e) => {
                              e.stopPropagation();
                              if (applyingIntentId !== item.intent.id) {
                                onApplyIntent?.(item.intent);
                              }
                            }}
                            opacity={
                              applyingIntentId === item.intent.id ? 0.8 : 1
                            }
                            cursor={
                              applyingIntentId === item.intent.id
                                ? "wait"
                                : undefined
                            }
                            pointerEvents={
                              applyingIntentId === item.intent.id
                                ? "none"
                                : undefined
                            }
                          >
                            <HStack gap={1.5} display="inline-flex">
                              {applyingIntentId === item.intent.id && (
                                <Spinner size="xs" />
                              )}
                              <span>
                                {applyingIntentId === item.intent.id
                                  ? "Applying…"
                                  : getInsightActionLabel(
                                      item.variant,
                                      "adset"
                                    )}
                              </span>
                            </HStack>
                          </CampaignDetailLink>
                          <Text color="gray.400" fontWeight="bold">
                            •
                          </Text>
                        </>
                      )}
                      <CampaignDetailLink
                        onClick={(e) => {
                          e.stopPropagation();
                          if (ignoringIntentId !== item.intent.id) {
                            onIgnoreIntent?.(item.intent);
                          }
                        }}
                        opacity={ignoringIntentId === item.intent.id ? 0.8 : 1}
                        cursor={
                          ignoringIntentId === item.intent.id
                            ? "wait"
                            : undefined
                        }
                        pointerEvents={
                          ignoringIntentId === item.intent.id
                            ? "none"
                            : undefined
                        }
                      >
                        <HStack gap={1.5} display="inline-flex">
                          {ignoringIntentId === item.intent.id && (
                            <Spinner size="xs" />
                          )}
                          <span>
                            {ignoringIntentId === item.intent.id
                              ? "Ignoring…"
                              : "Ignore"}
                          </span>
                        </HStack>
                      </CampaignDetailLink>
                    </HStack>
                  </HStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </MetricsCard>

      <Collapse in={isOpen}>
        <VStack align="stretch" spacing={4} pt={2}>
          {(adset.ads ?? []).length > 0 ? (
            (adset.ads ?? []).map((ad, index) => (
              <AdDetailBlock
                key={ad.id ?? ad.external_id ?? `ad-${index}`}
                ad={ad}
                onEditAdset={() => onEditAdset(adset.id)}
                onEditAd={onEditAd}
                adsetIdentity={adset.identity}
                pages={pages}
                instagramAccounts={instagramAccounts}
                intentsForAd={intentsForAd(intents, ad)}
                onApplyIntent={onApplyIntent}
                onIgnoreIntent={onIgnoreIntent}
                applyingIntentId={applyingIntentId}
                ignoringIntentId={ignoringIntentId}
                onOpenWizardReview={
                  onOpenWizardReview
                    ? () => onOpenWizardReview(adset.id)
                    : undefined
                }
                onPublishAd={
                  onPublishAd && (ad.id ?? ad.external_id)
                    ? () =>
                        onPublishAd({
                          campaign_id: campaign.id,
                          adset_id: adset.id,
                          ad_id: ad.id ?? ad.external_id ?? "",
                        })
                    : undefined
                }
                onUnpublishAd={
                  onUnpublishAd && (ad.id ?? ad.external_id)
                    ? () =>
                        onUnpublishAd({
                          campaign_id: campaign.id,
                          adset_id: adset.id,
                          ad_id: ad.id ?? ad.external_id ?? "",
                        })
                    : undefined
                }
                isPausingAd={
                  pausingAdKey === `${adset.id}:${ad.id ?? ad.external_id}`
                }
                isActivatingAd={
                  activatingAdKey === `${adset.id}:${ad.id ?? ad.external_id}`
                }
              />
            ))
          ) : (
            <Box
              bg="white"
              borderRadius="2xl"
              borderWidth="1px"
              borderColor="gray.200"
              p={4}
            >
              <Text fontSize="sm" color="gray.500" py={2}>
                No ads
              </Text>
            </Box>
          )}
        </VStack>
      </Collapse>

      <Box w="full" display="flex" justifyContent="center">
        <Button
          size="sm"
          borderRadius="6.25rem"
          borderWidth="1px"
          borderColor="#DDD"
          background="linear-gradient(180deg, rgba(231, 233, 239, 0.01) 0%, #FFF 100%), #FFF"
          color="gray.600"
          fontSize="xs"
          lineHeight="normal"
          fontWeight="500"
          h={6}
          px={2}
          gap={0}
          rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          onClick={onToggle}
          boxShadow="0 4px 12px -5px rgba(0, 0, 0, 0.04), 0 1px 2px 0 rgba(255, 255, 255, 0.75) inset"
          _hover={{ bg: "gray.200", borderColor: "gray.300" }}
        >
          {isOpen ? "See less" : "See more"}
        </Button>
      </Box>
    </VStack>
  );
}
